import { useEffect, useRef, useState } from 'react';
import { MascotLookError } from './client.js';
import { DESKTOP_LOOK_RECHECK_MS, LOOK_CLOSED_RECHECK_MS, LOOK_RECHECK_MS, LookTracker, browserLookStorage, desktopLookCheck, readLookMemory, writeLookMemory, } from './gyroLook.js';
import { MASCOT_LOOK_CHANGED_EVENT } from './mascotLooks.js';
/** A per-client number, so a new client (another app key, another person's auth) is another owner (review L8). */
const clientIds = new WeakMap();
let nextClientId = 1;
function clientId(client) {
    let id = clientIds.get(client);
    if (id === undefined) {
        id = nextClientId++;
        clientIds.set(client, id);
    }
    return id;
}
/**
 * The person's current Gyro look on this surface (#291; the timing is LookTracker's, gyroLook.ts), or null while there
 * is none to show yet: the caller shows the built-in Gyro then. The desktop reads it from the person's gallery
 * (forwarded under device custody, with the view's validator so an unchanged gallery is not sent again); every other
 * host asks GET /v1/mascot/current through its own auth. The last look is remembered per app and person, so the
 * closed launcher shows it as the page loads; a look never outlives the client, app or person it was read for.
 */
export function useGyroLook(options) {
    const { client, appKey, enabled, visible, open, memoryKey, rememberPictures, xl = false, onUnavailable, onDay } = options;
    const storage = options.storage === undefined ? browserLookStorage() : options.storage;
    const owner = `${clientId(client)}\n${appKey}\n${memoryKey ?? ''}`;
    const remembered = () => (rememberPictures ? readLookMemory(storage, memoryKey)?.look ?? null : null);
    const [state, setState] = useState(() => ({ owner, look: remembered() }));
    const tracker = useRef(null);
    const unavailable = useRef(onUnavailable);
    unavailable.current = onUnavailable;
    const dayAnswer = useRef(onDay);
    dayAnswer.current = onDay;
    const desktop = appKey === 'desktop';
    const interval = desktop ? DESKTOP_LOOK_RECHECK_MS : open ? LOOK_RECHECK_MS : LOOK_CLOSED_RECHECK_MS;
    useEffect(() => {
        if (!enabled)
            return undefined;
        // A different client, app or person: never show the previous one's look, only this one's remembered look.
        const initial = remembered();
        setState({ owner, look: initial });
        let held = initial;
        const apply = (look) => {
            held = look;
            setState({ owner, look });
            writeLookMemory(storage, memoryKey, { on: true, atMs: Date.now(), ...(rememberPictures ? { look } : {}) });
            dayAnswer.current?.(look.day ?? null);
        };
        const fetchLook = desktop
            ? (since) => desktopLookCheck(client, since)
            : async (since) => {
                try {
                    return await client.getLook(xl && held && held.look !== 'default' && !held.xlarge ? undefined : since, xl ? { xl: true } : {});
                }
                catch (error) {
                    // The service has the look off (or not for this app): forget it, show the built-in Gyro, stop asking.
                    if (error instanceof MascotLookError && error.status === 404) {
                        writeLookMemory(storage, memoryKey, { on: false, atMs: Date.now() });
                        setState({ owner, look: null });
                        unavailable.current?.();
                    }
                    throw error;
                }
            };
        const instance = new LookTracker({
            fetch: fetchLook,
            apply,
            intervalMs: interval,
            // Coming back on screen on the desktop waits for the interval: each read there costs a Graph call (review L1).
            ...(desktop ? { refocusMinMs: DESKTOP_LOOK_RECHECK_MS } : {}),
            initialEtag: initial?.etag,
        });
        tracker.current = instance;
        // A pick in Settings or under a reply in this window: show it now, not at the next check.
        const changed = () => instance.refresh();
        // The window regaining focus, or the page returning from the back/forward cache, counts like coming on screen: a tab
        // that was never hidden gets no visibility change, so a pick made in the desktop app would otherwise wait for the
        // next interval (up to 15 minutes; the owner, 2026-10-30). LookTracker keeps the one-minute floor and any backoff.
        const refocus = () => instance.refocus();
        window.addEventListener(MASCOT_LOOK_CHANGED_EVENT, changed);
        window.addEventListener('focus', refocus);
        window.addEventListener('pageshow', refocus);
        return () => {
            window.removeEventListener(MASCOT_LOOK_CHANGED_EVENT, changed);
            window.removeEventListener('focus', refocus);
            window.removeEventListener('pageshow', refocus);
            instance.dispose();
            if (tracker.current === instance)
                tracker.current = null;
        };
        // `interval` is applied below without restarting the tracker; `remembered` reads the same key as `owner`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, appKey, enabled, memoryKey, rememberPictures, xl]);
    useEffect(() => {
        tracker.current?.setVisible(enabled && visible);
    }, [enabled, visible, client, appKey, memoryKey, rememberPictures, xl]);
    useEffect(() => {
        tracker.current?.setInterval(interval);
    }, [interval, client, appKey, enabled, memoryKey, rememberPictures, xl]);
    if (!enabled)
        return null;
    return state.owner === owner ? state.look : remembered();
}
//# sourceMappingURL=useGyroLook.js.map